import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import {
  activateOperationalContext,
  getOperationalClients,
  getOperationalProductsByClient,
  getSessionUserNoCache,
} from '../../services/operationalContextService'
import {
  AdminHero,
  AdminSectionCard,
} from '../../components/settings/AdminConsoleShell'

const hasGlobalAdminRole = (roles) => {
  if (!Array.isArray(roles)) return false
  return roles.some((role) => {
    const type = typeof role === 'string' ? role : role?.type
    const name = typeof role === 'string' ? role : role?.name_role
    return type === 'globalAdmin' || name === 'globalAdmin'
  })
}

const OperationalContextManager = () => {
  const navigate = useNavigate()
  const { refreshUser } = useAuth()

  const [sessionUser, setSessionUser] = useState(null)
  const [loadingUser, setLoadingUser] = useState(false)

  const [clients, setClients] = useState([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState('')

  const [products, setProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [selectedProductId, setSelectedProductId] = useState('')

  const [activating, setActivating] = useState(false)
  const [feedback, setFeedback] = useState({ severity: '', message: '' })

  const isGlobalAdmin = useMemo(
    () => hasGlobalAdminRole(sessionUser?.roles),
    [sessionUser]
  )

  useEffect(() => {
    const loadUser = async () => {
      setLoadingUser(true)
      try {
        const user = await getSessionUserNoCache()
        setSessionUser(user)
      } catch (error) {
        setSessionUser(null)
        setFeedback({
          severity: 'error',
          message: 'No fue posible obtener la sesión actual.',
        })
      } finally {
        setLoadingUser(false)
      }
    }

    loadUser()
  }, [])

  useEffect(() => {
    const loadClients = async () => {
      if (!isGlobalAdmin) return

      setLoadingClients(true)
      try {
        const list = await getOperationalClients()
        setClients(list)
      } catch (error) {
        setClients([])
        setFeedback({
          severity: 'error',
          message: 'No fue posible cargar clientes para contexto operativo.',
        })
      } finally {
        setLoadingClients(false)
      }
    }

    loadClients()
  }, [isGlobalAdmin])

  useEffect(() => {
    const loadProducts = async () => {
      if (!isGlobalAdmin || !selectedClientId) {
        setProducts([])
        setSelectedProductId('')
        return
      }

      setLoadingProducts(true)
      try {
        const list = await getOperationalProductsByClient(selectedClientId)
        setProducts(list)
      } catch (error) {
        setProducts([])
        setSelectedProductId('')
        setFeedback({
          severity: 'error',
          message: 'No fue posible cargar productos del cliente seleccionado.',
        })
      } finally {
        setLoadingProducts(false)
      }
    }

    loadProducts()
  }, [isGlobalAdmin, selectedClientId])

  const handleActivate = async () => {
    if (!selectedClientId || !selectedProductId) {
      setFeedback({
        severity: 'warning',
        message: 'Selecciona cliente y producto antes de activar el contexto.',
      })
      return
    }

    setActivating(true)
    try {
      await activateOperationalContext({
        clientId: selectedClientId,
        productId: selectedProductId,
      })
      await refreshUser()

      const user = await getSessionUserNoCache()
      setSessionUser(user)
      setFeedback({
        severity: 'success',
        message: 'Contexto operativo activado correctamente.',
      })
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: 'No se pudo activar el contexto operativo.',
      })
    } finally {
      setActivating(false)
    }
  }

  if (loadingUser) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <CircularProgress size={22} />
        <Typography variant="body2">Resolviendo sesión actual…</Typography>
      </Stack>
    )
  }

  if (!isGlobalAdmin) {
    return (
      <Alert severity="warning">
        Esta vista está disponible únicamente para usuarios globalAdmin.
      </Alert>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <AdminHero
        eyebrow="Operational context"
        title="Contexto Operativo"
        description="Selecciona cliente y producto para activar el contexto operativo oficial antes de continuar con módulos sensibles."
        stats={[
          { label: 'Clientes', value: clients.length },
          { label: 'Productos', value: products.length },
          { label: 'Estado', value: sessionUser?.productId ? 'Activo' : 'Pendiente' },
        ]}
        actions={
          <Button
            variant="outlined"
            onClick={() => navigate('/settings/controlpanel/layout-manager')}
          >
            Ir a Layouts
          </Button>
        }
      />

      <AdminSectionCard
        title="Activación de contexto"
        subtitle="Este es el punto oficial para activar el contexto operativo globalAdmin."
      >
        <Stack spacing={2}>
          {feedback.message && (
            <Alert severity={feedback.severity || 'info'}>
              {feedback.message}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            Contexto activo actual: clientId {sessionUser?.clientId || '—'} / productId {sessionUser?.productId || '—'}.
          </Typography>

          {loadingClients ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="body2">Cargando clientes…</Typography>
            </Stack>
          ) : (
            <TextField
              select
              fullWidth
              label="Cliente"
              value={selectedClientId}
              onChange={(event) => {
                setSelectedClientId(event.target.value)
                setSelectedProductId('')
              }}
            >
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name_client || `Cliente ${client.id}`}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            fullWidth
            label="Producto"
            value={selectedProductId}
            onChange={(event) => setSelectedProductId(event.target.value)}
            disabled={!selectedClientId || loadingProducts}
          >
            {products.map((product) => (
              <MenuItem key={product.id} value={product.id}>
                {product.name_product || `Producto ${product.id}`}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            onClick={handleActivate}
            disabled={!selectedClientId || !selectedProductId || activating}
          >
            {activating ? 'Activando contexto…' : 'Activar contexto operativo'}
          </Button>
        </Stack>
      </AdminSectionCard>
    </Box>
  )
}

export default OperationalContextManager
